import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Autocomplete, Box, Button, FormControlLabel, Grid, MenuItem, Stack, Switch, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { useSnackbar } from '../../components/context/SnackbarContext';
import useAxios from '../../hooks/useAxios';
import { useLoader } from "../../components/context/LoaderContext";
import CreateQuoteItem from "./components/CreateQuoteItem";
import { capitalize } from "../../utils/Utils";
import DynamicFields from "components/common/CustomFields";

function AddQuote(props) {
    const navigate = useNavigate();
    const axiosInstance = useAxios();
    const { showSnackbar } = useSnackbar();
    const { showLoader, hideLoader } = useLoader();

    const [userList, setUserList] = useState([]);
    const [dealList, setDealList] = useState([]);
    const [accountList, setAccountList] = useState([]);
    const [contactList, setContactList] = useState([]);
    const [countries, setCountries] = useState([]);
    const [productList, setProductList] = useState([]);
    const [quoteStageList, setQuoteStageList] = useState([]);
    const [isShippingSameAsBilling, setIsShippingSameAsBilling] = useState(false);
    const [customFields, setCustomFields] = useState([]);
    const [customQuoteItemFields, setCustomQuoteItemFields] = useState([]);
    const [customQuoteBillingFields, setCustomQuoteBillingFields] = useState([]);
    const [customQuoteShippingFields, setCustomQuoteShippingFields] = useState([]);

    useEffect(() => {
        async function fetchData() {
            getData();
        }
        fetchData();
    }, []);


    const getData = async () => {
        showLoader();
        await axiosInstance.get('api/quote/create').then((response) => {
            if (response.status === 200) {
                setUserList(response?.data?.data?.users || []);
                setDealList(response?.data?.data?.deals || []);
                setAccountList(response?.data?.data?.accounts || []);
                setContactList(response?.data?.data?.contacts || []);
                setCountries(response?.data?.data?.countries || []);
                setProductList(response?.data?.data?.products || []);
                setQuoteStageList(response?.data?.data?.quoteStageLists || []);
                setCustomFields(response?.data?.data?.customFieldVariables?.customFields || []);
                setCustomQuoteItemFields(response?.data?.data?.customFieldVariables?.quoteItemcustomFields || [])
                setCustomQuoteBillingFields(response?.data?.data?.customFieldVariables?.quoteBillingCustomFields || [])
                setCustomQuoteShippingFields(response?.data?.data?.customFieldVariables?.quoteShippinfCustomFields || [])
                hideLoader();
            }
        })
            .then((json) => { })
            .catch((error) => {
                if (error.response) {
                    hideLoader();
                    if (error.response.status === 400) {
                        showSnackbar(error.response.data.message, "warning");
                        navigate("/quotes");
                    }
                }
            });
    }

    const buildInitialValues = ({ customFields, customQuoteBillingFields, customQuoteShippingFields }) => {
        const initial = {
            subject: '',
            user_id: '',
            account_id: '',
            contact_id: '',
            deal_id: '',
            quote_stage: '',
            owner_id: '',

            address: '',
            city: '',
            state: '',
            zipcode: '',
            country_id: '',

            is_same_billing: '',
            s_address: '',
            s_city: '',
            s_state: '',
            s_zipcode: '',
            s_country_id: '',

            terms_and_condition: '',
            description: '',

            productId: '',
            quote_final_total: '',
            quote_item_array: [],
            error_list: [],
        };

        // Quote fields
        customFields?.forEach(field => {
            const key = field.field_name;
            initial[key] = getDefaultValueByType(field.display_type);
        });

        // Billing fields - with billing_ prefix
        customQuoteBillingFields?.forEach(field => {
            const key = `billing_${field.field_name}`;
            initial[key] = getDefaultValueByType(field.display_type);
        });

        // Shipping fields - with shipping_ prefix
        customQuoteShippingFields?.forEach(field => {
            const key = `shipping_${field.field_name}`;
            initial[key] = getDefaultValueByType(field.display_type);
        });

        return initial;
    };

    const getDefaultValueByType = (type) => {
        switch (type) {
            case 'checkbox':
            case 'file':
                return [];
            case 'date':
            case 'datetime':
            default:
                return '';
        }
    };


    const buildValidationSchema = ({ customFields, customQuoteBillingFields = [], customQuoteShippingFields = [] }) => {
        const schema = {
            subject: yup.string().required('Enter subject').min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters'),
            account_id: yup.string().required('Select Account'),
            deal_id: yup.string().required('Select Deal'),

            address: yup.string().min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters'),
            city: yup.string().min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters'),
            state: yup.string().min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters'),
            zipcode: yup.string().min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters'),

            s_address: yup.string().min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters'),
            s_city: yup.string().min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters'),
            s_state: yup.string().min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters'),
            s_zipcode: yup.string().min(2, 'Must be at least 2 characters').max(50, 'Must be at most 50 characters'),

            terms_and_condition: yup.string().min(2, 'Must be at least 2 characters').max(150, 'Must be at most 150 characters'),
            description: yup.string().min(2, 'Must be at least 2 characters').max(150, 'Must be at most 150 characters'),
        };

        const processCustomFields = (fields, prefix = '') => {
            fields.forEach(field => {
                const key = prefix ? `${prefix}_${field.field_name}` : field.field_name;
                const label = field?.label || field?.field_name;
                const isRequired = field?.is_required === 'yes';

                switch (field?.display_type) {
                    case 'text':
                    case 'textarea':
                        schema[key] = yup.string();
                        if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                        break;

                    case 'email':
                        schema[key] = yup.string().email('Enter a valid email');
                        if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                        break;

                    case 'phone':
                        schema[key] = yup
                            .string()
                            .matches(/^[0-9()+-\s]*$/, 'Enter a valid phone number');
                        if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                        break;

                    case 'number':
                        schema[key] = yup
                            .number()
                            .typeError(`${label} must be a number`);
                        if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                        break;

                    case 'select':
                        if (field?.is_multiple === 'yes') {
                            // MULTI-SELECT
                            schema[key] = yup.array().of(yup.string());
                            if (isRequired) {
                                schema[key] = schema[key].min(1, `Select at least one ${label}`);
                            }
                        } else {
                            // SINGLE SELECT
                            schema[key] = yup.string();
                            if (isRequired) {
                                schema[key] = schema[key].required(`${label} is required`);
                            }
                        }
                        break;
                    case 'radio':
                        schema[key] = yup.string();
                        if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                        break;

                    case 'checkbox':
                        schema[key] = yup.array();
                        if (isRequired) schema[key] = schema[key].min(1, `Select at least one ${label}`);
                        break;

                    case 'date':
                    case 'datetime':
                        schema[key] = yup.string();
                        if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                        break;

                    case 'file':
                        schema[key] = yup.array().of(
                            yup
                                .mixed()
                                .test('fileSize', 'File too large', value => !value || value.size <= 4 * 1024 * 1024)
                        );
                        if (isRequired) {
                            schema[key] = schema[key].min(1, `Upload at least one ${label}`);
                        }
                        break;

                    default:
                        schema[key] = yup.string();
                        if (isRequired) schema[key] = schema[key].required(`${label} is required`);
                        break;
                }
            });
        };

        processCustomFields(customFields);
        processCustomFields(customQuoteBillingFields, 'billing');
        processCustomFields(customQuoteShippingFields, 'shipping');

        return yup.object().shape(schema);
    };

    const staticFields = ['subject', 'contact_id', 'account_id', 'deal_id', 'quote_stage', 'owner_id', 'address', 'city', 'state',
        'zipcode', 'country_id', 'is_same_billing', 's_address', 's_city', 's_state', 's_zipcode', 's_country_id', 'terms_and_condition',
        'description', 'quote_item_array', 'quote_final_total', 'user_id', 'productId', 'error_list', 'quote_item_array', 'quote_final_total', 'finalData'];

    const getContactData = async (id, setFieldValue, type) => {
        if (id) {
            await axiosInstance.get(`api/quote/getAddress/${id}?type=${type}`).then(response => {
                if (response.status === 200) {
                    if (type === 'deal') {
                        setAccountList(response?.data?.data?.getAccountLists || [])
                        setContactList(response?.data?.data?.getContactLists || [])

                        if (response?.data?.data?.getContactLists?.length === 0) {
                            setFieldValue('address', '')
                            setFieldValue('city', '')
                            setFieldValue('state', '')
                            setFieldValue('zipcode', '')
                            setFieldValue('country_id', '')
                        }
                    } else {
                        setFieldValue('address', response?.data?.data?.getBillingAddress?.address1 || '')
                        setFieldValue('city', response?.data?.data?.getBillingAddress?.city || '')
                        setFieldValue('state', response?.data?.data?.getBillingAddress?.state || '')
                        setFieldValue('zipcode', response?.data?.data?.getBillingAddress?.zipcode || '')
                        setFieldValue('country_id', response?.data?.data?.getBillingAddress?.country_id || '')
                    }
                }
            }).then(() => {

            }).catch(error => {
                if (error.response) {
                    hideLoader()
                    showSnackbar(error.response.data.message, 'error')
                }
            });
        } else {
            setAccountList([])
            setContactList([])

            setFieldValue('address', '')
            setFieldValue('city', '')
            setFieldValue('state', '')
            setFieldValue('zipcode', '')
            setFieldValue('country_id', '')
        }

    }

    const handleSwitchChange = (event, setFieldValue, props) => {
        setIsShippingSameAsBilling(event.target.checked);
        setFieldValue('is_same_billing', event.target.checked)
        if (event.target.checked) {
            setFieldValue('s_address', props.values.address ? props.values.address : '')
            setFieldValue('s_city', props.values.city ? props.values.city : '')
            setFieldValue('s_state', props.values.state ? props.values.state : '')
            setFieldValue('s_zipcode', props.values.zipcode ? props.values.zipcode : '')
            setFieldValue('s_country_id', props.values.country_id ? props.values.country_id : '')
        } else {
            setFieldValue('s_address', '')
            setFieldValue('s_city', '')
            setFieldValue('s_state', '')
            setFieldValue('s_zipcode', '')
            setFieldValue('s_country_id', '')
        }
    };


    const handleSubmit = async (values) => {
        const formData = new FormData();

        const customFieldsData = {};
        const customQuoteBillingFieldsData = {};
        const customQuoteShippingFieldsData = {};

        Object.entries(values).forEach(([key, value]) => {
            if (staticFields.includes(key)) {
                // Static fields (e.g., first_name, etc.)
                if (Array.isArray(value)) {
                    value.forEach(file => formData.append(`${key}[]`, file));
                } else {
                    formData.append(key, value);
                }
            } else if (customFields.some(f => f.field_name === key)) {
                // Quote custom fields
                if (Array.isArray(value) && value.length && value[0] instanceof File) {
                    value.forEach((file, idx) => {
                        formData.append(`${key}[${idx}]`, file); // upload quote files
                    });
                } else {
                    customFieldsData[key] = value;
                }
                // } else if (customQuoteBillingFields.some(f => f.field_name === key)) {
                //     // Billing custom fields
                //     if (Array.isArray(value) && value.length && value[0] instanceof File) {
                //         value.forEach((file, idx) => {
                //             formData.append(`${key}[${idx}]`, file); // upload billing files
                //         });
                //     } else {
                //         customQuoteBillingFieldsData[key] = value;
                //     }
                // } else if (customQuoteShippingFields.some(f => f.field_name === key)) {
                //     // Billing custom fields
                //     if (Array.isArray(value) && value.length && value[0] instanceof File) {
                //         value.forEach((file, idx) => {
                //             formData.append(`${key}[${idx}]`, file); // upload billing files
                //         });
                //     } else {
                //         customQuoteShippingFieldsData[key] = value;
                //     }
                // }
            } else if (key.startsWith('billing_')) {
                const actualKey = key.replace('billing_', '');
                if (Array.isArray(value) && value.length && value[0] instanceof File) {
                    value.forEach((file, idx) => {
                        formData.append(`${key}[${idx}]`, file); // upload billing files
                    });
                } else {
                    customQuoteBillingFieldsData[actualKey] = value;
                }
            } else if (key.startsWith('shipping_')) {
                const actualKey = key.replace('shipping_', '');
                if (Array.isArray(value) && value.length && value[0] instanceof File) {
                    value.forEach((file, idx) => {
                        formData.append(`${key}[${idx}]`, file); // upload shipping files
                    });
                } else {
                    customQuoteShippingFieldsData[actualKey] = value;
                }
            }

        });

        formData.append('quote_item_array', JSON.stringify(values.QuoteItemArray));
        formData.append('quote_final_total', JSON.stringify(values.finalData));

        const customFieldsPayload = {
            quote: customFieldsData,
            billing: customQuoteBillingFieldsData,
            shipping: customQuoteShippingFieldsData
        };

        formData.append('custom_fields', JSON.stringify(customFieldsPayload));

        showLoader();
        await axiosInstance.post("/api/quote/store", formData).then(response => {
            if (response.status === 200) {
                showSnackbar(response.data.message, "success");
                navigate("/quotes");
            }
        }).then(json => {

        }).catch(error => {
            hideLoader();
            if (error.response) {
                hideLoader()
                if (error.response.status === 400 || error.response.status === 300) {
                    showSnackbar(error?.response?.data?.message, "warning");
                } else {
                    showSnackbar('Oops Something went wrong..!', "warning");
                }
            }
        });
    }

    const quoteDetail = []


    return (
        <>
            <Helmet>
                <title>Add Quote | {capitalize(props?.name || 'SolvCRM')}</title>
            </Helmet>
            <Formik
                initialValues={buildInitialValues({ customFields, customQuoteBillingFields: customQuoteBillingFields, customQuoteShippingFields: customQuoteShippingFields })}
                validationSchema={buildValidationSchema({ customFields, customQuoteBillingFields: customQuoteBillingFields, customQuoteShippingFields: customQuoteShippingFields })}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {props => {
                    const {
                        touched,
                        errors,
                        values,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        setFieldValue,
                        setFieldTouched
                    } = props;
                    return (
                        <>
                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={2}>


                                    <Grid item xxl={4} xs={12}>
                                        <div className="card p-16 height-100p pb-0">
                                            <Typography variant="title-medium-large-semibold" component={'h4'} className="mb-16">Quote Information</Typography>

                                            <Grid container rowSpacing={0} columnSpacing={1.5}>
                                                <Grid item xxl={12} xs={6}>
                                                    <TextField
                                                        label="Subject"
                                                        variant="filled"
                                                        name="subject"
                                                        value={values?.subject || ''}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={errors.subject && touched.subject}
                                                        helperText={(errors.subject && touched.subject) && errors.subject}
                                                    />

                                                </Grid>

                                                <Grid item xxl={12} xs={6}>
                                                    <Autocomplete
                                                        options={dealList || []}
                                                        getOptionLabel={(option) => option.deal_name || ''}
                                                        value={dealList.find(deal => deal.id === values?.deal_id) || ''}
                                                        onChange={(event, newValue) => {
                                                            const e = {
                                                                target: {
                                                                    name: 'deal_id',
                                                                    value: newValue ? newValue.id : ''
                                                                }
                                                            };
                                                            handleChange(e);
                                                            getContactData(e.target.value, setFieldValue, 'deal')
                                                            setContactList([]);
                                                            setAccountList([]);
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Deal"
                                                                variant="filled"
                                                                error={errors.deal_id && touched.deal_id}
                                                                helperText={(errors.deal_id && touched.deal_id) && errors.deal_id}
                                                                onBlur={handleBlur}
                                                                name="deal_id"
                                                            />
                                                        )}
                                                    />
                                                </Grid>



                                                <Grid item xxl={12} xs={6}>
                                                    <TextField
                                                        select
                                                        label="Account"
                                                        value={values?.account_id || ''}
                                                        name="account_id"
                                                        variant="filled"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={errors.account_id && touched.account_id}
                                                        helperText={(errors.account_id && touched.account_id) && errors.account_id}
                                                    >
                                                        {
                                                            accountList && accountList.length > 0
                                                                ? accountList.map((account, index) => (
                                                                    <MenuItem key={index} value={account?.id}>
                                                                        {account?.organization_name}
                                                                    </MenuItem>
                                                                ))
                                                                : <MenuItem value={'-1'}></MenuItem>
                                                        }
                                                    </TextField>
                                                </Grid>
                                                <Grid item xxl={12} xs={6}>
                                                    <TextField
                                                        select
                                                        label="Contact"
                                                        value={values?.contact_id || ''}
                                                        name="contact_id"
                                                        variant="filled"
                                                        onChange={(e) => { handleChange(e); getContactData(e.target.value, setFieldValue, 'contact') }}
                                                        onBlur={handleBlur}
                                                        error={errors.contact_id && touched.contact_id}
                                                        helperText={(errors.contact_id && touched.contact_id) && errors.contact_id}
                                                    >
                                                        {
                                                            contactList && contactList.length > 0
                                                                ? contactList.map((contact, index) => (
                                                                    <MenuItem key={index} value={contact?.id}>
                                                                        {contact?.salutation ? contact.salutation + '. ' : ''}{contact?.first_name} {contact?.last_name}
                                                                    </MenuItem>
                                                                ))
                                                                : <MenuItem value={'-1'}></MenuItem>
                                                        }
                                                    </TextField>
                                                </Grid>
                                                <Grid item xxl={12} xs={6}>
                                                    <TextField
                                                        select
                                                        label="Quote Stage"
                                                        value={values?.quote_stage || ''}
                                                        name="quote_stage"
                                                        variant="filled"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={errors.quote_stage && touched.quote_stage}
                                                        helperText={(errors.quote_stage && touched.quote_stage) && errors.quote_stage}
                                                    >
                                                        <MenuItem key="all-sources" value="">
                                                            Select Stage
                                                        </MenuItem>
                                                        {
                                                            quoteStageList &&
                                                            Object.keys(quoteStageList).map((index) => (
                                                                <MenuItem key={index} value={quoteStageList[index]}>
                                                                    {quoteStageList[index]}
                                                                </MenuItem>
                                                            ))
                                                        }
                                                    </TextField>
                                                </Grid>
                                                <Grid item xxl={12} xs={6}>
                                                    <TextField
                                                        select
                                                        label="Quote Owner"
                                                        value={values?.owner_id || ''}
                                                        name="owner_id"
                                                        variant="filled"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={errors.owner_id && touched.owner_id}
                                                        helperText={(errors.owner_id && touched.owner_id) && errors.owner_id}
                                                    >
                                                        <MenuItem key="all-sources" value="" >
                                                            Select Owner
                                                        </MenuItem>
                                                        {
                                                            userList && userList.length > 0
                                                                ? userList.map((user, index) => (
                                                                    <MenuItem key={index} value={user?.id}>
                                                                        {user?.name}
                                                                    </MenuItem>
                                                                ))
                                                                : <MenuItem value={'-1'}></MenuItem>
                                                        }
                                                    </TextField>
                                                </Grid>

                                                {
                                                    customFields?.length > 0 &&
                                                    <>
                                                        <Typography variant="title-medium-large-semibold" component={'h4'} className="ml-12 mb-12">Custom Fields</Typography>
                                                        <Grid item xxl={12} xs={12}></Grid>
                                                        <Grid item xxl={12} xs={6}>
                                                            {customFields.map((field, idx) => (
                                                                <DynamicFields
                                                                    key={idx}
                                                                    field={field}
                                                                    values={values}
                                                                    handleChange={handleChange}
                                                                    handleBlur={handleBlur}
                                                                    errors={errors}
                                                                    touched={touched}
                                                                    setFieldValue={setFieldValue}
                                                                />
                                                            ))}
                                                        </Grid>
                                                    </>
                                                }
                                            </Grid>
                                        </div>
                                    </Grid>

                                    <Grid item xxl={8} xs={12}>
                                        <div className="card p-16 height-100p pb-0">
                                            <Typography variant="title-medium-large-semibold" component={'h4'} className="mb-16">Address Information</Typography>

                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <Typography variant="title-small-bold" component={'h4'} className="mb-8">Billing Address</Typography>
                                                    <TextField
                                                        label="Street"
                                                        variant="filled"
                                                        name="address"
                                                        value={values?.address || ''}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={errors.address && touched.address}
                                                        helperText={(errors.address && touched.address) && errors.address}
                                                    />

                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                label="City"
                                                                variant="filled"
                                                                name="city"
                                                                value={values?.city || ''}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={errors.city && touched.city}
                                                                helperText={(errors.city && touched.city) && errors.city}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                label="State"
                                                                variant="filled"
                                                                name="state"
                                                                value={values?.state || ''}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={errors.state && touched.state}
                                                                helperText={(errors.state && touched.state) && errors.state}
                                                            />
                                                        </Grid>
                                                    </Grid>

                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                label="Postal Code"
                                                                variant="filled"
                                                                name="zipcode"
                                                                value={values?.zipcode || ''}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={errors.zipcode && touched.zipcode}
                                                                helperText={(errors.zipcode && touched.zipcode) && errors.zipcode}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                select
                                                                label="Country"
                                                                value={values?.country_id || ''}
                                                                name="country_id"
                                                                variant="filled"
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={errors.country_id && touched.country_id}
                                                                helperText={(errors.country_id && touched.country_id) && errors.country_id}
                                                            >
                                                                {
                                                                    countries && countries.length > 0
                                                                        ? countries.map((country, index) => (
                                                                            <MenuItem key={index} value={country?.id}>
                                                                                {country?.country_name}
                                                                            </MenuItem>
                                                                        ))
                                                                        : <MenuItem value={'-1'}></MenuItem>
                                                                }
                                                            </TextField>
                                                        </Grid>
                                                    </Grid>
                                                    {
                                                        customQuoteBillingFields?.length > 0 &&
                                                        <>
                                                            <Typography variant="title-medium-large-semibold" component={'h4'}>Custom Fields</Typography>
                                                            <Grid container spacing={2}>
                                                                <Grid item xxl={12} xs={12}></Grid>
                                                                {customQuoteBillingFields.map((field, idx) => (
                                                                    <Grid item xxl={12} xs={field?.display_type === 'textarea' ? 12 : 6} key={idx}>
                                                                        <DynamicFields
                                                                            key={idx}
                                                                            //field={field}
                                                                            field={{ ...field, field_name: `billing_${field.field_name}` }}
                                                                            values={values}
                                                                            handleChange={handleChange}
                                                                            handleBlur={handleBlur}
                                                                            errors={errors}
                                                                            touched={touched}
                                                                            setFieldValue={setFieldValue}
                                                                        />
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        </>
                                                    }
                                                    <div className="form-group">
                                                        <FormControlLabel control={
                                                            <Switch
                                                                checked={isShippingSameAsBilling}
                                                                onChange={(event) => handleSwitchChange(event, setFieldValue, props)}
                                                            />} label="Shipping address same as billing" />
                                                    </div>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="title-small-bold" component={'h4'} className="mb-8">Shipping Address</Typography>

                                                    <TextField
                                                        label="Street"
                                                        variant="filled"
                                                        name="s_address"
                                                        value={values?.s_address || ''}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={errors.s_address && touched.s_address}
                                                        helperText={(errors.s_address && touched.s_address) && errors.s_address}
                                                    />

                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                label="City"
                                                                variant="filled"
                                                                name="s_city"
                                                                value={values?.s_city || ''}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={errors.s_city && touched.s_city}
                                                                helperText={(errors.s_city && touched.s_city) && errors.s_city}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                label="State"
                                                                variant="filled"
                                                                name="s_state"
                                                                value={values?.s_state || ''}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={errors.s_state && touched.s_state}
                                                                helperText={(errors.s_state && touched.s_state) && errors.s_state}
                                                            />
                                                        </Grid>
                                                    </Grid>

                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                label="Postal Code"
                                                                variant="filled"
                                                                name="s_zipcode"
                                                                value={values?.s_zipcode || ''}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={errors.s_zipcode && touched.s_zipcode}
                                                                helperText={(errors.s_zipcode && touched.s_zipcode) && errors.s_zipcode}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                select
                                                                label="Country"
                                                                value={values?.s_country_id || ''}
                                                                name="s_country_id"
                                                                variant="filled"
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                error={errors.s_country_id && touched.s_country_id}
                                                                helperText={(errors.s_country_id && touched.s_country_id) && errors.s_country_id}
                                                            >
                                                                {
                                                                    countries && countries.length > 0
                                                                        ? countries.map((country, index) => (
                                                                            <MenuItem key={index} value={country?.id}>
                                                                                {country?.country_name}
                                                                            </MenuItem>
                                                                        ))
                                                                        : <MenuItem value={'-1'}></MenuItem>
                                                                }
                                                            </TextField>
                                                        </Grid>
                                                    </Grid>
                                                    {
                                                        customQuoteShippingFields?.length > 0 &&
                                                        <>
                                                            <Typography variant="title-medium-large-semibold" component={'h4'}>Custom Fields</Typography>
                                                            <Grid container spacing={2}>
                                                                <Grid item xxl={12} xs={12}></Grid>
                                                                {customQuoteShippingFields.map((field, idx) => (
                                                                    <Grid item xxl={12} xs={field?.display_type === 'textarea' ? 12 : 6}>
                                                                        <DynamicFields
                                                                            key={idx}
                                                                            //field={field}
                                                                            field={{ ...field, field_name: `shipping_${field.field_name}` }}
                                                                            values={values}
                                                                            handleChange={handleChange}
                                                                            handleBlur={handleBlur}
                                                                            errors={errors}
                                                                            touched={touched}
                                                                            setFieldValue={setFieldValue}
                                                                        />
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        </>
                                                    }
                                                </Grid>
                                            </Grid>
                                        </div>
                                    </Grid>

                                    <CreateQuoteItem data={{ setFieldValue, values, setFieldTouched, productList, quoteDetail, errors, touched, customQuoteItemFields }} />

                                    <Grid item xs={12}>
                                        <div className="card p-16">
                                            <Stack direction="row" spacing={1.5} justifyContent={'flex-end'}>
                                                <Button type="submit">Create Quote</Button>
                                                <Button color="secondary" onClick={e => { navigate('/quotes'); }}>Cancel</Button>
                                            </Stack>
                                        </div>
                                    </Grid>

                                </Grid>
                            </form>
                        </>
                    );
                }}
            </Formik>
        </>
    );
}

export default AddQuote;